/* Copyright (c) 2025 MOCA Systems, Inc.  All rights reserved. */
import { AbstractApplication } from '@appCanvasCommon/core/AbstractApplication';
import { CanvasAngularLand } from '../../../../app-data/interfaces/canvas-angular-land';
import { LayerPaths } from '@appPlanApplication/LayerPaths';
import { takeUntil } from 'rxjs/operators';
import { TODAY_LINE_LABEL_TEXT } from '@touchplan/strings';
import { ITodayLine } from '../../../../modules/plan/services/today-line.service';
import { cssVar } from '@touchplan/css-var';
import { IDestroyOptions } from '@appCanvasCommon/core/DestroyOptions';
import { AbstractDisplayLine } from '@appPlanApplication/timeline/AbstractDisplayLine';
/**
 * TodayLineDrawer is responsible for rendering the today line indicator in the canvas.
 * The today line shows the current date with a vertical line and text label.
 */
export class TodayLineDrawer extends AbstractDisplayLine {
	/**
	 * We store the last ITodayLine as a cache to handle cases where the canvas is forced to redraw
	 * The primary reason for this is due to TP-6818, where the Canvas onResize$ observable emits before the camera and renderer are resized
	 * Can be cleaned up after TP-6818 so the draw() method called through a combined observable, but requires thinking through how resize should work on the AbstractApplication
	 * @private
	 */
	private _cachedTodayLine: ITodayLine | null = null;
	/**
	 * Creates a new TodayLineDrawer instance.
	 * @param _canvasAngularLand - Interface to Angular services/dependencies
	 * @param _application - The canvas application instance
	 */
	constructor(private _canvasAngularLand: CanvasAngularLand, _application: AbstractApplication) {
		super(_application, LayerPaths.BACKGROUND_TIMELINE_TODAY_LINE);
		this.setupSubscribers();
	}
	/**
	 * Updates the today line's position based on camera transforms.
	 * This ensures the line stays correctly positioned during pan/zoom operations.
	 * @param _ - Delta Time elapsed since last update
	 */
	public update(_?: number): void {
		if (!this._container || !this._cachedTodayLine) {
			return;
		}
		super.update(
			this.parentApplication.camera.realToScreenX(this._cachedTodayLine.x),
			cssVar.headerHeight_px
		);
	}
	/**
	 * Draws or updates the today line visualization.
	 * Creates a vertical line and positions the rotated date label.
	 * @param todayLine - Today line data to render, defaults to cached data
	 */
	public draw(todayLine: ITodayLine | null = this._cachedTodayLine): void {
		if (!todayLine) {
			this.hide();
			return;
		}

		this._label.text = `${TODAY_LINE_LABEL_TEXT} ${todayLine.date}`;
		super.draw();
		this._cachedTodayLine = todayLine;
		// We are updating the position of the visual in update(). Since draw() can cause the position of the line
		// to change, we need to explicitly invoke update to ensure the update is positioned correctly.
		// If we don't do this, then the line will be out of position until the next tick (any type of interaction with the canvas)
		// In the case of the today line, this could be a very jarring update if the plan was left open for a few days
		this.update();
	}
	/**
	 * Cleans up resources when the drawer is destroyed.
	 */
	public destroy(opts?: IDestroyOptions): void {
		super.destroy(opts);
		this._canvasAngularLand = null;
	}
	/**
	 * Sets up subscription to today line updates from the service.
	 * Triggers redraws when the today line data changes.
	 */
	private setupSubscribers(): void {
		this._canvasAngularLand.todayLineService.changed$
			.pipe(takeUntil(this._shutdownSubject$))
			.subscribe((todayLine) => this.draw(todayLine));

		this.parentApplication.camera.changed$.pipe(takeUntil(this._shutdownSubject$)).subscribe((_) => {
			this.update();
		});
	}
}
